import { useState } from 'react'
import { SessionMark } from '../sessions/SessionMark'
import type { MemberRow } from './membership'

function Face({ name, small = false }: { name: string; small?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground ${small ? 'size-3 text-[7px]' : 'size-6 text-[10px]'}`}
    >
      {name.trim().slice(0, 2).toUpperCase()}
    </span>
  )
}

/** One trigger, including the overlapping Share pill: every part opens the same list. */
export function MemberStack({
  rows,
  open,
  kind,
  onClick,
}: {
  rows: MemberRow[]
  open: boolean
  kind: string
  onClick: () => void
}) {
  const present = rows
    .filter((r) => r.present && (r.kind === 'person' || r.kind === 'agent'))
    .sort((a, b) => Number(a.kind === 'agent') - Number(b.kind === 'agent'))
  const shown = present.slice(0, 3)
  return (
    <button
      type="button"
      aria-label={`Share ${kind}`}
      aria-expanded={open}
      aria-haspopup="dialog"
      onClick={onClick}
      className="flex h-7 shrink-0 items-center rounded-full hover:bg-muted active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      <span className="flex items-center pl-1.5">
        {shown.map((row) => (
          <span
            key={row.id}
            title={`${row.name}: ${row.reason}`}
            className="-ml-1.5 rounded-full ring-2 ring-background"
          >
            {row.kind === 'agent' ? (
              <SessionMark
                id={row.sessionId!}
                size={24}
                badge={<Face name={row.personName || '?'} small />}
              />
            ) : (
              <Face name={row.name} />
            )}
          </span>
        ))}
      </span>
      {present.length > shown.length && (
        <span className="px-1 text-[11px] tabular-nums">
          +{present.length - shown.length}
        </span>
      )}
      <span
        className={`${shown.length ? '-ml-1' : ''} relative rounded-full border border-border bg-background px-2.5 py-0.5 text-xs`}
      >
        Share
      </span>
    </button>
  )
}

/** A person the viewer can take back out, and what stays with them. */
export interface Removal {
  /** Said in the confirm, e.g. what another share still gives them. */
  keeps?: string
  /** What they are removed from, "this page" unless said. */
  where?: string
  /** Read from signed grants when the confirm opens (S12): what leaves, what
   * stays and who may also be removed. A blocked removal offers no Remove. */
  preview?: () => Promise<RemovalPreview>
  /** Called with the ids picked from the preview's `offers`. */
  remove: (alsoRemove?: string[]) => Promise<void>
}

export interface RemovalPreview {
  lines: string[]
  /** People they invited, kept unless picked. */
  offers: Array<{ id: string; name: string }>
  blocked?: string
}

export function MemberList({
  rows,
  error,
  removalFor,
}: {
  rows: MemberRow[]
  error?: string
  /** Absent, or undefined for a row, means no Remove is offered. */
  removalFor?: (row: MemberRow) => Removal | undefined
}) {
  if (error)
    return (
      <p role="status" className="text-xs text-muted-foreground">
        {error}
      </p>
    )
  return (
    <div className="max-h-72 space-y-3 overflow-y-auto">
      {(['person', 'agent', 'group', 'key'] as const).map((kind) => {
        const entries = rows.filter((row) => row.kind === kind)
        if (!entries.length && kind !== 'person' && kind !== 'agent')
          return null
        const heading = {
          person: 'People',
          agent: 'Agents',
          group: 'Groups',
          key: 'Other keys',
        }[kind]
        return (
          <section key={kind} aria-label={heading} className="space-y-1">
            <h3 className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
              {heading}
            </h3>
            {entries.length ? (
              entries.map((row) => (
                <div
                  key={row.id}
                  className="flex items-center gap-2 py-1"
                  title={row.id}
                >
                  {row.kind === 'agent' ? (
                    <SessionMark
                      id={row.sessionId!}
                      size={24}
                      badge={<Face name={row.personName || '?'} small />}
                    />
                  ) : (
                    <Face name={row.name} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{row.name}</div>
                    <p className="break-words text-[11px] text-muted-foreground">
                      {row.personName ? `${row.personName}'s · ` : ''}
                      {row.reason}
                      {row.present
                        ? row.kind === 'agent'
                          ? ' · working here'
                          : ' · here now'
                        : ''}
                    </p>
                  </div>
                  {removalFor?.(row) && (
                    <RemoveMember name={row.name} removal={removalFor(row)!} />
                  )}
                </div>
              ))
            ) : (
              <p className="text-xs text-muted-foreground">
                No identified {heading.toLowerCase()}.
              </p>
            )}
          </section>
        )
      })}
    </div>
  )
}

/** Remove, then a confirm that says what the person keeps. */
function RemoveMember({ name, removal }: { name: string; removal: Removal }) {
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<RemovalPreview>()
  const [also, setAlso] = useState<string[]>([])
  if (!asking)
    return (
      <button
        type="button"
        className="shrink-0 rounded px-1 text-muted-foreground text-xs hover:bg-muted hover:text-foreground"
        onClick={() => {
          setAsking(true)
          setPreview(undefined)
          setAlso([])
          setError('')
          removal
            .preview?.()
            .then(setPreview)
            .catch((e: unknown) =>
              setError(e instanceof Error ? e.message : 'Could not check'),
            )
        }}
      >
        Remove
      </button>
    )
  const waiting = !!removal.preview && !preview
  const blocked = preview?.blocked
  return (
    <div
      role="alertdialog"
      aria-label={`Remove ${name}`}
      className="w-44 shrink-0 space-y-1 text-[11px]"
    >
      <p>
        Remove {name} from {removal.where ?? 'this page'}? What is already on
        their devices stays readable to them.
        {removal.keeps ? ` ${removal.keeps}` : ''}
      </p>
      {waiting && !error && <p className="text-muted-foreground">Checking…</p>}
      {preview?.lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
      {preview?.offers.map((offer) => (
        <label key={offer.id} className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={also.includes(offer.id)}
            onChange={(e) =>
              setAlso((had) =>
                e.target.checked
                  ? [...had, offer.id]
                  : had.filter((id) => id !== offer.id),
              )
            }
          />
          Also remove {offer.name}
        </label>
      ))}
      {blocked && <p role="alert">Cannot remove yet: {blocked}</p>}
      {error && <p role="alert">{error}</p>}
      <div className="flex gap-1">
        <button
          type="button"
          disabled={busy || waiting || !!blocked}
          className="rounded border border-border px-1.5 hover:bg-muted"
          onClick={() => {
            setBusy(true)
            setError('')
            removal
              .remove(also)
              .catch((e: unknown) =>
                setError(e instanceof Error ? e.message : 'Could not remove'),
              )
              .finally(() => setBusy(false))
          }}
        >
          {busy ? 'Removing…' : 'Remove'}
        </button>
        <button
          type="button"
          disabled={busy}
          className="rounded px-1.5 text-muted-foreground hover:bg-muted"
          onClick={() => setAsking(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
